import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Building2, Wifi, KeyRound, Coffee, ArrowUpDown, Utensils } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "/img/leasing_hero.png";
const SECOND_IMG = "/img/leasing_two.png";
const THIRD_IMG = "/img/leasing_three.png";
const EXTRA_IMG = "/img/hotdesk_row.png"; // hot-desk row — also used here per owner

const FEATURES = [
  "Access to toilets up- and down-stairs",
  "Fully-functioning kitchen with lunch desk setup",
  "Access to the shared Saba boardroom",
  "Stairs and lifts to the boardroom",
  "Coffee machine and pantry",
  "Separate Wi-Fi access",
  "Secure coded entry — code & key issued on arrival",
  "In the heart of the Toowoomba CBD",
  "Walking distance to plenty of foodary",
  "Right next to Grand Central shopping centre",
];

const initialForm = {
  name: "", email: "", phone: "", company: "",
  headcount: "", preferred_move_in: "", message: "",
};

const inputCls =
  "w-full bg-transparent border-b border-[#E6DFD3] py-3 text-[#2D231E] font-sans placeholder-[#A88B6C] focus:outline-none focus:border-[#4A3C31] transition-colors rounded-none";
const labelCls =
  "block text-[11px] font-sans tracking-[0.25em] uppercase text-[#5D4037] mb-2";

export default function LeasingPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email) {
      setError("Please fill in name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        headcount: form.headcount ? Number(form.headcount) : null,
      };
      await axios.post(`${API}/leasing-enquiries`, payload);
      setSubmitted(true);
      setForm(initialForm);
    } catch {
      setError("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="leasing-page" className="animate-in fade-in duration-500">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-12 sm:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-6">Service 03 · Toowoomba</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#2D231E] leading-[1.02] mb-6">
              Tenant <span className="italic text-[#8B7355]">Leasing</span>
            </h1>
            <p className="font-serif text-xl sm:text-2xl italic text-[#4A3C31] max-w-2xl mb-5">
              Move your team into the Saba building.
            </p>
            <p className="font-sans text-base text-[#5D4037] max-w-xl">
              Bright, character-filled commercial spaces in the heart of the
              Toowoomba CBD — exposed brick, lofty ceilings, natural light, and
              everything your team needs to land and get to work.
            </p>
            <a href="#leasing-enquire" className="mt-7 inline-flex items-center justify-center px-7 py-3.5 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm">
              Register Interest
            </a>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#E6DFD3]">
              <img src={HERO_IMG} alt="Open-plan commercial leasing space" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Gallery + features */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7 mb-6 lg:mb-8">
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#E6DFD3]">
            <img src={SECOND_IMG} alt="Saba leasing space — brick walls and natural light" className="w-full h-full object-cover" />
          </div>
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#E6DFD3]">
            <img src={THIRD_IMG} alt="Saba leasing space — second tenancy area" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="mb-12 lg:mb-16">
          <div className="relative aspect-[21/9] overflow-hidden rounded-sm border border-[#E6DFD3]">
            <img src={EXTRA_IMG} alt="Open work area within Saba Workspace" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-3">What's on offer</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2D231E] mb-5">A turnkey home for your team.</h2>
            <p className="font-sans text-sm sm:text-base text-[#5D4037] max-w-md">
              Shared amenities across the Saba building, secure access, and a
              location that puts your team a few minutes from anything they need.
            </p>
            <p className="font-sans text-xs italic text-[#8B7355] mt-5">
              Exact tenancy sizing available on request.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul data-testid="leasing-features" className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 font-sans text-sm text-[#5D4037]">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <span className="mt-2 inline-block w-1 h-1 rounded-full bg-[#8B7355] shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-7">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><Building2 size={12} strokeWidth={1.5}/>CBD Location</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><ArrowUpDown size={12} strokeWidth={1.5}/>Stairs + Lift</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><Utensils size={12} strokeWidth={1.5}/>Kitchen + Pantry</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><Coffee size={12} strokeWidth={1.5}/>Coffee Machine</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><Wifi size={12} strokeWidth={1.5}/>Separate Wi-Fi</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><KeyRound size={12} strokeWidth={1.5}/>Coded Entry + Key</span>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="leasing-enquire" className="max-w-7xl mx-auto px-6 sm:px-12 pb-24 sm:pb-32">
        <div className="bg-[#FAF9F6] border border-[#E6DFD3] rounded-sm p-8 sm:p-12">
          <div className="mb-8">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-3">Register Interest</p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#2D231E] mb-3">Talk to us about a tenancy.</h3>
            <p className="font-sans text-sm sm:text-base text-[#5D4037] max-w-lg">
              Tell us a little about your team and when you're looking to move
              in — we'll be in touch directly to arrange a walk-through.
            </p>
          </div>

          {submitted ? (
            <div data-testid="leasing-success" className="border border-[#E6DFD3] p-8 sm:p-10 rounded-sm bg-[#F9F6F0] flex flex-col items-start gap-4">
              <CheckCircle2 size={28} strokeWidth={1.5} className="text-[#8B7355]" />
              <h4 className="font-serif text-2xl sm:text-3xl text-[#2D231E]">Enquiry received.</h4>
              <p className="font-sans text-sm sm:text-base text-[#5D4037] max-w-md">
                Thanks for reaching out — someone from the Saba Workspace team will be
                in contact with you shortly to arrange a walk-through.
              </p>
              <button type="button" onClick={() => setSubmitted(false)} className="mt-2 inline-flex items-center justify-center px-6 py-3 bg-transparent border border-[#4A3C31] text-[#4A3C31] font-sans text-xs tracking-[0.25em] uppercase hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors rounded-sm">
                Send another
              </button>
            </div>
          ) : (
            <form data-testid="leasing-form" onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
              <div><label className={labelCls} htmlFor="name">Name</label><input id="name" name="name" value={form.name} onChange={onChange} required placeholder="Your name" data-testid="leasing-name" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="company">Company (optional)</label><input id="company" name="company" value={form.company} onChange={onChange} placeholder="Your business" data-testid="leasing-company" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="email">Email</label><input id="email" name="email" type="email" value={form.email} onChange={onChange} required placeholder="you@example.com" data-testid="leasing-email" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="phone">Phone (optional)</label><input id="phone" name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="04xx xxx xxx" data-testid="leasing-phone" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="headcount">Team size (optional)</label><input id="headcount" name="headcount" type="number" min={1} max={200} value={form.headcount} onChange={onChange} placeholder="e.g. 6" data-testid="leasing-headcount" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="preferred_move_in">Preferred move-in (optional)</label><input id="preferred_move_in" name="preferred_move_in" type="text" value={form.preferred_move_in} onChange={onChange} placeholder="e.g. Q1 next year" data-testid="leasing-movein" className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls} htmlFor="message">Tell us about your team</label><textarea id="message" name="message" rows={4} value={form.message} onChange={onChange} placeholder="What does your business do, what kind of space are you after, anything specific you'd like to know?" data-testid="leasing-message" className={`${inputCls} resize-none`} /></div>
              {error && <p data-testid="leasing-error" className="sm:col-span-2 font-sans text-sm text-[#9D5B5B]">{error}</p>}
              <div className="sm:col-span-2 flex justify-end">
                <button type="submit" disabled={submitting} data-testid="leasing-submit" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm disabled:opacity-60">
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {submitting ? "Sending…" : "Send Enquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
